import React, { useEffect, useMemo, useState } from "react";
import { getUniversityContacts } from "../../Redux/service/DashboardService";
import {
  Box,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import ResponsiveDrawer from "../../component/sidebar/sidebar.component";
import CustomDataTable from "../../component/datatable/DataTable";
import { CopyToClipboard } from "../../component/CopyToClipboard";
import "./contactList.css";

// CustomDataTable always renders the selection checkbox column, so its width has
// to be part of the minimum table width or the last column gets squeezed.

const copyCell = (field, label, minWidth) => ({
  field,
  headerName: label,
  flex: 1.2,
  minWidth,
  renderCell: (params) => {
    const value = params.value || "";

    return (
      <Box className="contact-copy-cell">
        <span className="contact-copy-cell__value">{value || "-"}</span>
        <Tooltip title={`Copy ${label.toLowerCase()}`}>
          <span>
            <IconButton
              size="small"
              aria-label={`Copy ${label.toLowerCase()}`}
              disabled={!value}
              onClick={(event) => {
                event.stopPropagation();
                CopyToClipboard(value);
              }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  },
});

// `priority` drives which columns survive on smaller screens:
// 1 = always, 2 = tablet and up, 3 = wide screens only.
//
// The two tabs describe different things, so they get different columns: an
// organisation contact has no "Main contact" flag and no university
// designation, and showing those headers over empty cells was just misleading.
const UNIVERSITY_COLUMNS = [
  {
    field: "entityName",
    headerName: "University Name",
    flex: 1.2,
    minWidth: 180,
    priority: 1,
  },
  { field: "name", headerName: "Name", flex: 1, minWidth: 150, priority: 1 },
  { ...copyCell("email", "Email", 200), priority: 1 },
  {
    field: "designation",
    headerName: "Designation",
    flex: 1,
    minWidth: 160,
    priority: 2,
  },
  {
    field: "mainContact",
    headerName: "Main Contact - Yes/No",
    flex: 0.8,
    minWidth: 170,
    priority: 3,
  },
  {
    field: "universityDesignation",
    headerName: "University Designation",
    flex: 1,
    minWidth: 190,
    priority: 3,
  },
  { ...copyCell("phoneNo", "Phone No.", 170), priority: 1 },
];

const ORGANISATION_COLUMNS = [
  {
    field: "entityName",
    headerName: "Organisation Name",
    flex: 1.2,
    minWidth: 190,
    priority: 1,
  },
  { field: "name", headerName: "Name", flex: 1, minWidth: 150, priority: 1 },
  { ...copyCell("email", "Email", 200), priority: 1 },
  {
    field: "designation",
    headerName: "Role",
    flex: 0.9,
    minWidth: 150,
    priority: 2,
  },
  { ...copyCell("phoneNo", "Phone No.", 170), priority: 1 },
  // {
  //   field: "location",
  //   headerName: "Location",
  //   flex: 1,
  //   minWidth: 180,
  //   priority: 3,
  // },
];

const ContactPage = () => {
  const [activeTab, setActiveTab] = useState("university");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [cardName, setCardName] = useState("UNIVERSITY CONTACTS");
  const [loading, setLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isNarrow = useMediaQuery(theme.breakpoints.down("lg"));
  const maxPriority = isMobile ? 1 : isNarrow ? 2 : 3;

  const columns = useMemo(() => {
    const source =
      activeTab === "organisation" ? ORGANISATION_COLUMNS : UNIVERSITY_COLUMNS;

    return source
      .filter((column) => column.priority <= maxPriority)
      .map(({ priority, ...column }) => column);
  }, [maxPriority, activeTab]);


  useEffect(() => {
    let isCurrentRequest = true;
    const requestType = activeTab === "university" ? "U" : "O";
      setRows([]);
    // 1500ms delayed even the first paint. Only typing needs debouncing.
    const debounce = search.trim() ? 400 : 0;

    const timer = setTimeout(async () => {
      setLoading(true);
     
      try {
        const response = await getUniversityContacts({
          type: requestType,
          search: search.trim(),
        });

        if (isCurrentRequest) {
          const responseData = response?.data;
          const contactRows = Array.isArray(responseData?.data)
            ? responseData.data
            : Array.isArray(responseData)
              ? responseData
              : [];

          setCardName(responseData?.cardName || "UNIVERSITY CONTACTS");
          setRows(
            contactRows.map((contact, index) => ({
              id: contact._id || contact.id || `${requestType}-${index}`,
              entityName:
                contact.entityName ||
                contact.universityName ||
                contact.organizationName ||
                "",
              mainContact: contact.mainContact || "",
              universityDesignation: contact.universityDesignation || "",
              phoneNo: contact.phone || contact.phoneNo || "",
              email:contact.email,
              name:contact.name,
              designation: contact.designation || "",
              location: contact.location || "",
            }))
          );
        }
      } catch (error) {
        console.error("Unable to load university contacts:", error);
        if (isCurrentRequest) setRows([]);
      } finally {
        if (isCurrentRequest) setLoading(false);
      }
    }, debounce);

    return () => {
      isCurrentRequest = false;
      clearTimeout(timer);
    };
  }, [activeTab, search]);

  return (
    <Box className="contact-page">
      <Box className="contact-header">
        <Typography className="contact-title" component="h1">
          {cardName}
        </Typography>
        <TextField
          className="contact-search"
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search contacts"
          aria-label="Search contacts"
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" />,
          }}
        />
      </Box>

      <Tabs
        className="contact-tabs"
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        variant={isMobile ? "fullWidth" : "standard"}
        aria-label="Contact type"
      >
        <Tab value="university" label="University" />
        <Tab value="organisation" label="Organisation" />
      </Tabs>

      {/* The table fills whatever vertical space is left; contactList.css owns the
          height so it can also neutralise the fixed 70vh/1200px that
          CustomDataTable applies inline. `checkboxSelection` and
          `showSelectionSummary` are currently ignored by CustomDataTable. */}
      <Box className="contact-table">
        <CustomDataTable
          row={rows}
          columns={columns}
          loading={loading}
          checkboxSelection={false}
          showSelectionSummary={false}
          height="100%"
        />
      </Box>
    </Box>
  );
};

const Contact = () => (
  <ResponsiveDrawer maincontent={<ContactPage />} />
);

export default Contact;
